import React from 'react';
import { Edit2, Trash2, Copy, Paperclip, ArrowRightLeft } from 'lucide-react';
import { CATEGORY_LABELS } from '@/constants/categories';
import { Checkbox } from '@/components/ui/checkbox';

export function EntriesTable({ entries, userRole, siteMap, onEdit, onDelete, onCopy, onOpenAttachments, onRowClick, selectedIds, onToggleSelect, onToggleSelectAll }) {
  const isEditor = ['admin', 'manager'].includes(userRole);
  const getSiteName = (id) => siteMap[id] || 'Unknown Site';
  const allSelected = entries.length > 0 && selectedIds?.size === entries.length;
  const someSelected = selectedIds?.size > 0 && !allSelected;

  return (
    <div className="bg-card border border-border overflow-x-auto -mx-2 sm:mx-0" data-testid="entries-table">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-border bg-muted">
            {isEditor && (
              <th className="py-3 px-3 w-10">
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={() => onToggleSelectAll && onToggleSelectAll()}
                  data-testid="select-all-entries"
                />
              </th>
            )}
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Date</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Site</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Type</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Category</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Item</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Agency / Entity</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Mode</th>
            <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Amount</th>
            {isEditor && <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr><td colSpan={isEditor ? 11 : 8} className="text-center py-12 text-muted-foreground">No entries found</td></tr>
          ) : entries.map(entry => (
            <tr key={entry._id} className={`border-b border-border hover:bg-muted/50 transition-colors cursor-pointer ${selectedIds?.has(entry._id) ? 'bg-primary/5' : ''} ${entry.dummy_account_name || (entry.sub_category || '').includes('Dummy Account') ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`} data-testid={`entry-row-${entry._id}`} onClick={() => onRowClick && onRowClick(entry)}>
              {isEditor && (
                <td className="py-3 px-3" onClick={e => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds?.has(entry._id) || false}
                    onCheckedChange={() => onToggleSelect && onToggleSelect(entry._id)}
                    data-testid={`select-entry-${entry._id}`}
                  />
                </td>
              )}
              <td className="py-3 px-4 whitespace-nowrap">{entry.date}</td>
              <td className="py-3 px-4 whitespace-nowrap">{getSiteName(entry.site_id)}</td>
              <td className="py-3 px-4">
                <span className={`text-xs font-medium px-2 py-0.5 inline-block ${entry.entry_type === 'income' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'}`}>
                  {entry.entry_type === 'income' ? 'Money In' : 'Money Out'}
                </span>
                {entry.transfer_id && (
                  <span className="text-xs px-1.5 py-0.5 ml-1 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 inline-flex items-center gap-0.5">
                    <ArrowRightLeft className="w-2.5 h-2.5" />Transfer
                  </span>
                )}
              </td>
              <td className="py-3 px-4 capitalize">{CATEGORY_LABELS[entry.category] || entry.category}</td>
              <td className="py-3 px-4">{entry.sub_category || '-'}</td>
              <td className="py-3 px-4">{entry.entity_name || '-'}</td>
              <td className="py-3 px-4 capitalize">{entry.payment_mode}</td>
              <td className={`py-3 px-4 text-right font-mono font-medium ${entry.entry_type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                {'\u20B9'}{entry.amount.toLocaleString('en-IN')}
              </td>
              {isEditor && (
                <td className="py-3 px-4 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  <button onClick={() => onOpenAttachments(entry._id)} className="p-1.5 hover:bg-muted rounded" title="Attachments" data-testid={`attachments-entry-${entry._id}`}><Paperclip className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onCopy(entry)} className="p-1.5 hover:bg-muted rounded" title="Copy entry" data-testid={`copy-entry-${entry._id}`}><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onEdit(entry)} className="p-1.5 hover:bg-muted rounded" data-testid={`edit-entry-${entry._id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onDelete(entry._id)} className="p-1.5 hover:bg-muted rounded text-red-600 ml-1" data-testid={`delete-entry-${entry._id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
